// Source: https://ui.mantine.dev/component/navbar-nested

import React, { useState } from "react";
import {
  Box,
  Collapse,
  Group,
  Indicator,
  Menu,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { ChevronRight } from "tabler-icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./NavbarLinkGroup.module.css";

const BRAND = "#46BDE1";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function LinksGroup({
  icon: Icon,
  label,
  initiallyOpened,
  links,
  link,
  hasNumber,
  collapsed = false,
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const hasLinks = Array.isArray(links) && links.length > 0;
  const [opened, setOpened] = useState(initiallyOpened || false);

  const isActive =
    (link && (pathname === link || pathname.startsWith(`${link}/`))) ||
    (hasLinks &&
      links.some(
        (item) =>
          pathname === item.link || pathname.startsWith(`${item.link}/`),
      ));

  const goTo = (target) => {
    navigate(target);
  };

  const iconButton = (
    <ThemeIcon
      variant="light"
      color="cyan"
      size={collapsed ? 36 : 28}
      radius="md"
    >
      <Icon size={collapsed ? 18 : 16} color={BRAND} />
    </ThemeIcon>
  );

  if (collapsed) {
    if (hasLinks) {
      return (
        <Menu withinPortal position="right-start" offset={8} withArrow>
          <Menu.Target>
            <Tooltip label={label} position="right">
              <UnstyledButton
                className={classNames(
                  styles.control,
                  styles.controlCollapsed,
                  isActive && styles.controlActive,
                )}
              >
                {hasNumber ? (
                  <Indicator
                    label={hasNumber}
                    size={16}
                    color="cyan"
                    offset={4}
                    inline
                  >
                    {iconButton}
                  </Indicator>
                ) : (
                  iconButton
                )}
              </UnstyledButton>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label className={styles.capitalize}>{label}</Menu.Label>
            {links.map((item) => (
              <Menu.Item
                key={item.label}
                className={styles.capitalize}
                onClick={() => goTo(item.link)}
              >
                {item.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <Tooltip label={label} position="right">
        <UnstyledButton
          onClick={() => link && goTo(link)}
          className={classNames(
            styles.control,
            styles.controlCollapsed,
            isActive && styles.controlActive,
          )}
        >
          {hasNumber ? (
            <Indicator
              label={hasNumber}
              size={16}
              color="cyan"
              offset={4}
              inline
            >
              {iconButton}
            </Indicator>
          ) : (
            iconButton
          )}
        </UnstyledButton>
      </Tooltip>
    );
  }

  const subLinks = (hasLinks ? links : []).map((itemLink) => {
    const subActive =
      pathname === itemLink.link || pathname.startsWith(`${itemLink.link}/`);

    return (
      <Text
        component="button"
        type="button"
        className={classNames(
          styles.link,
          styles.subLink,
          subActive && styles.linkActive,
        )}
        key={itemLink.label}
        onClick={() => goTo(itemLink.link)}
      >
        {itemLink.label}
      </Text>
    );
  });

  return (
    <>
      <UnstyledButton
        onClick={() => {
          if (hasLinks) {
            setOpened((o) => !o);
            return;
          }
          if (link) goTo(link);
        }}
        className={classNames(styles.control, isActive && styles.controlActive)}
      >
        <Group justify="space-between" gap={0} wrap="nowrap">
          <Box className={styles.iconRow}>
            {iconButton}
            <Box ml="sm" className={styles.labelRow}>
              <span>{label}</span>
              {hasNumber ? (
                <span className={styles.badge}>{hasNumber}</span>
              ) : null}
            </Box>
          </Box>
          {hasLinks && (
            <ChevronRight
              className={classNames(
                styles.chevron,
                opened && styles.chevronOpen,
              )}
              size={14}
            />
          )}
        </Group>
      </UnstyledButton>
      {hasLinks ? <Collapse in={opened}>{subLinks}</Collapse> : null}
    </>
  );
}
